import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Reveal } from "@/components/motion/Reveal";
import { InstagramLogo, ArrowRight, Play, ImageSquare, SquaresFour } from "@phosphor-icons/react";

const MEDIA_TYPE_ICON = {
    VIDEO: Play,
    CAROUSEL_ALBUM: SquaresFour,
    IMAGE: ImageSquare,
};

export function InstagramTile({ post }) {
    const image = post.media_type === "VIDEO" ? (post.thumbnail_url || post.media_url) : post.media_url;
    const Icon = MEDIA_TYPE_ICON[post.media_type] || ImageSquare;
    return (
        <a
            href={post.permalink}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`instagram-tile-${post.id}`}
            className="tz-card group relative block aspect-square overflow-hidden bg-black border border-zinc-800 hover:border-[#FFD000] transition-colors"
        >
            {image ? (
                <img
                    src={image}
                    alt={(post.caption || "Instagram post").slice(0, 120)}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
            ) : (
                <div className="w-full h-full grid place-items-center text-zinc-600">
                    <InstagramLogo size={32} />
                </div>
            )}
            <div className="absolute top-2 right-2 w-7 h-7 grid place-items-center bg-black/70 text-[#FFD000] backdrop-blur-sm">
                <Icon size={14} weight="bold" />
            </div>
            <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black/90 via-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div className="text-white text-xs font-mono uppercase tracking-[0.2em] flex items-center gap-1">
                    View <ArrowRight size={10} weight="bold" />
                </div>
            </div>
        </a>
    );
}

// Preview mode: pass `previewPosts` to render fixed data (used in admin preview).
// Live mode: fetches /api/instagram/posts.
export default function InstagramFeed({ previewPosts }) {
    const [posts, setPosts] = useState(previewPosts || []);
    const [loading, setLoading] = useState(!previewPosts);
    const [username, setUsername] = useState("tizonatech");

    useEffect(() => {
        if (previewPosts) return;
        let active = true;
        (async () => {
            try {
                const { data } = await api.get("/instagram/posts");
                if (!active) return;
                setPosts(Array.isArray(data?.posts) ? data.posts.slice(0, 12) : []);
                if (data?.username) setUsername(data.username);
            } catch {
                if (active) setPosts([]);
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => { active = false; };
    }, [previewPosts]);

    if (loading) return null;

    return (
        <section className="border-t border-zinc-800 mt-24" data-testid="instagram-feed">
            <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20">
                <Reveal className="flex flex-wrap items-end justify-between border-b border-zinc-800 pb-6 mb-10 gap-6">
                    <div>
                        <div className="overline mb-3 flex items-center gap-2">
                            <InstagramLogo size={14} weight="bold" /> // On Instagram
                        </div>
                        <h2 className="font-display text-5xl md:text-6xl text-white tracking-tight leading-none">
                            @{(username || "tizonatech").toUpperCase()}<span className="text-[#FFD000]">.</span>
                        </h2>
                    </div>
                    <a
                        href={`https://www.instagram.com/${username || "tizonatech"}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-follow-link"
                        className="font-mono text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-[#FFD000] inline-flex items-center gap-2 link-slide"
                    >
                        Follow on Instagram <ArrowRight size={14} weight="bold" />
                    </a>
                </Reveal>

                {posts.length === 0 ? (
                    <a
                        href="https://www.instagram.com/tizonatech"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="instagram-empty-cta"
                        className="block group bg-[#0A0A0A] border border-zinc-800 hover:border-[#FFD000] transition-colors p-12 md:p-16 text-center"
                    >
                        <InstagramLogo size={48} weight="light" className="text-[#FFD000] mx-auto mb-6" />
                        <div className="font-display text-3xl md:text-4xl text-white mb-3">Follow us on Instagram</div>
                        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-zinc-500 mb-6">
                            @tizonatech — Latest posts, behind the scenes, community
                        </div>
                        <span className="btn-primary inline-flex items-center gap-2 group-hover:translate-y-[-2px] transition-transform">
                            Open Instagram <ArrowRight size={14} weight="bold" />
                        </span>
                    </a>
                ) : (
                    <div
                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4"
                        data-testid="instagram-grid"
                    >
                        {posts.map((p) => <InstagramTile key={p.id} post={p} />)}
                    </div>
                )}
            </div>
        </section>
    );
}
